import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Params } from '@angular/router';
import { CustomSearchBarConfig, CustomTableData, CustomTableListInfos, GeneralListDataResponse, TableFilterData } from '@app/models';
import { RouteParams } from '@app/helpers';
import { CustomTableFilterComponent } from '@app/shared/components/custom-table-filter/custom-table-filter.component';
import { SearchBarComponent, TOOLTIP_DELAY } from '@app/shared';
import { CommonService } from '@app/core';
import { paginationHelper } from '@app/shared/data/helpers';


@Component({
    selector: 'app-custom-table-list',
    templateUrl: './custom-table-list.component.html',
    styleUrls: ['./custom-table-list.component.scss'],
    standalone: false
})
export class CustomTableListComponent implements OnInit, OnDestroy {
    tooltipDelay = TOOLTIP_DELAY.DATA_TABLE;
    private readonly otherParams = ['limit', 'page', 'order', 'orderBy', 'searchTerm', 'searchText'];

    @Input('componentData') componentData!: CustomTableData<any>;
    @Input('customTableListInfos') customTableListInfos!: CustomTableListInfos;
    @Output('fetchList') fetchList: EventEmitter<any> = new EventEmitter<any>();
    dataSource!: MatTableDataSource<any>;
    pageSizeOptions = [20, 50, 100];
    recordLength: number | undefined;
    tableFiltersData: TableFilterData[] = [];
    tableFilterKeyOrder: string[] = [];
    tableFilterOpen: boolean = false;
    filterParams!: RouteParams; 
    searchBarConfig!: CustomSearchBarConfig;
    noRecord: boolean = true;
    
    @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort!: MatSort;
    @ViewChild('customTableFiltersComp') customTableFiltersComp!: CustomTableFilterComponent;
    @ViewChild('searchBarCompTemp') searchBarCompTemp!: SearchBarComponent;

    constructor(
        private readonly _commonService: CommonService
    ) {
    }

    public ngAfterViewInit() {
        if (this.componentData.listData) {
            this.paginator.pageSize = this.componentData.listData.limit;
            this.paginator.pageIndex = this.componentData.listData.page - 1;
        }
        this.sort.sortChange.subscribe(() => {
            this.paginator.pageIndex = 0;
            this.paginator.pageSize = 20;
            this.fetchListData({ order: this.sort.direction, orderBy: this.sort.active });
        });
        this.paginator.page.subscribe(() => {
            this.fetchListData({ limit: this.paginator.pageSize, page: this.paginator.pageIndex + 1 });
        });
    }

    ngOnInit(): void {
        this.createTable();
    }
    
    ngOnDestroy() {
        this.resetAll(true);
        this.searchBarConfig.input.value = '';
        this.searchBarCompTemp?.resetAction();
        this.customTableFiltersComp?.createForm(this.componentData.filtersData);
        if (this.paginator) {
            this.paginator.pageSize = 20;
            this.paginator.pageIndex = 1;
        }
        if (this.sort) {
            this.sort.active = this.componentData.filterParams.defaultSortBy || '';
            this.sort.direction = 'desc';
        }
        this.resetRouteParams({limit: 20, page: 1});
        this.dataSource.data = [];
        this.recordLength = 0;
        this.noRecord = true;
    }

    createTable(data?: CustomTableData<any>) {
      if (data) this.componentData = {...data};

      this.resetRouteParams({ ...this.componentData.filterParams.filteredParams });
      if (this.componentData.listData) {
        if (!this.dataSource) this.dataSource = new MatTableDataSource();
        this.dataSource.data = [...this.componentData.listData.data.rows];
        this.recordLength = this.componentData.listData.data.count;
        if (this.paginator) {
            this.paginator.pageSize = this.componentData.listData.limit;
            this.paginator.pageIndex = this.componentData.listData.page - 1;
        }
        this.noRecord = (this.componentData.listData.page == 1) && (this.recordLength == 0) 
            && (!this.filterParams.pageParams.hasOwnProperty('searchText') && !this.hasAnyFilter);
      } else this.noRecord = true;
      this.searchBarConfig = {...this.componentData.searchBarConfig};
      this.tableFilterKeyOrder = [...this.componentData.filtersKeyOrder];
      this.tableFiltersData = [...this.componentData.filtersData];
      this.resetFilterTable();

    }

    updateContent(listData: GeneralListDataResponse<any>) {
        if (listData) {
          if (!this.dataSource) this.dataSource = new MatTableDataSource();
          this.dataSource.data = [...listData.data.rows];
          this.recordLength = listData.data.count;
          if (this.paginator) {
              this.paginator.pageSize = listData.limit;
              this.paginator.pageIndex = listData.page - 1;
          }
          this.noRecord = (listData.page == 1) && (this.recordLength == 0) 
              && (!this.filterParams.pageParams.hasOwnProperty('searchText') && !this.hasAnyFilter);
        } else this.noRecord = true;
    }

    private fetchListData(newParams: { [k:string]: any }, clearAll: boolean = false) {
        this.resetRouteParams(clearAll ? {...newParams} : { ...this.filterParams.pageParams, ...newParams });
        this.fetchList.emit();
    }

    searchUsers() {
        this.searchBarConfig.input.value = this.searchBarConfig.input.value?.trim();
        this.customTableFiltersComp?.createForm(this.componentData.filtersData);
        if (this.paginator) {
            this.paginator.pageSize = 20;
            this.paginator.pageIndex = 1;
        }
        if (this.sort) {
            this.sort.active = this.componentData.filterParams.defaultSortBy || '';
            this.sort.direction = 'desc';
        }
        this.fetchListData(this.searchValueParams, true);
    }

    resetAll(onlyReset: boolean = false) {
        this.searchBarConfig.input.value = '';
        if (this.searchBarConfig.searchByOptions) this.searchBarConfig.selectedSearchByOptions = this.searchBarConfig.searchByOptions[0];
        this.searchBarCompTemp?.resetAction();
        this.customTableFiltersComp?.createForm(this.componentData.filtersData);
        if (this.paginator) {
            this.paginator.pageSize = 20;
            this.paginator.pageIndex = 1;
        }
        if (this.sort) {
            this.sort.active = this.componentData.filterParams.defaultSortBy || '';
            this.sort.direction = 'desc';
        }
        this.resetRouteParams({limit: 20, page: 1});
        if (!onlyReset) this.fetchListData({ limit: 20, page: 1 }, true);
    }

    filterTable(params: Params) {
        if (this.paginator) {
            this.paginator.pageSize = 20;
            this.paginator.pageIndex = 1;
        }
        if (this.sort) {
            this.sort.active = this.componentData.filterParams.defaultSortBy || '';
            this.sort.direction = 'desc';
        }
        this.fetchListData({ ...this.searchValueParams, ...params }, true);
    }

    resetFilterTable() {
        if (this.hasAnyFilter) {
            this.tableFiltersData = this._commonService.setTableFilterValues(
                this.tableFilterKeyOrder,
                { ...this.filterParams.parseFilterPayload() },
                [...this.tableFiltersData]
            );
        } else this.tableFiltersData = [ ...this.componentData.filtersData ];
        this.tableFilterOpen = this.hasAnyFilter;
        this.customTableFiltersComp?.createForm(this.tableFiltersData);
    }

    resetRouteParams(params?: Params) {
        this.filterParams = new RouteParams(
            params || this.filterParams.pageParams, 
            (this.componentData?.filterParams?.defaultSortBy || ''), 
            this.tableFilterKeyOrder
        );
    }

    get currentPaginateSizes(): number[] {
        return paginationHelper(this.pageSizeOptions, this.recordLength);
    }

    private get searchValueParams(): {[k:string]: any} {
        let searchParams = {};
        if (this.searchBarConfig.input.value?.length)  {
            searchParams = { searchText: this.searchBarConfig.input.value };
            if (this.searchBarConfig?.selectedSearchByOptions?.value) {
                searchParams = { ...searchParams, searchTerm: this.searchBarConfig.selectedSearchByOptions.value };
            }
        }
        return searchParams;
    }

    get hasAnyFilter(): boolean {
        let fParams: any = {};
        if (this.filterParams?.pageParams) {
            for (const key in this.filterParams?.pageParams) {
                if (this.filterParams.pageParams.hasOwnProperty(key) && !this.otherParams.includes(key)) {
                    fParams[key] = this.filterParams.pageParams[key];
                }
            }
        }
      return JSON.stringify(fParams) != '{}';
    }

    /**Has any list search, filters, sort or paginate */
    get hasListFilters(): boolean {
        return (this.filterParams.pageParams.hasOwnProperty('searchText') 
            || this.hasAnyFilter 
            || this.currentSort.active != this.filterParams.defaultSortBy 
            || this.currentSort.direction != this.filterParams.defaultSortIn 
            || this.paginator?.pageIndex != (this.filterParams.defaultPage - 1) 
            || this.paginator?.pageSize != this.filterParams.defaultLimit
        );
    }

    get currentSort() {
        return {
            active: this.noRecord ? '' : this.filterParams.filteredParams?.orderBy || this.componentData.filterParams.defaultSortBy || '', 
            direction: this.noRecord ? '' : this.filterParams.filteredParams?.order || 'desc' 
        }
    }

    get displayedColumns(): string[] {
        return this.componentData.dataColumns.map(col => col.value);
    }

}
